import { z } from 'zod';
import { GradientField, portalTheme, ShadowStyle, } from '../schema/portalTheme.js';
import { channels } from './color.js';
import { fontWeights, isFontStack } from './fonts.js';
/**
 * Generates Custom CSS for the customer portal (`crew-customer-ui`).
 *
 * Everything it emits sits inside the portal's theming contract (`CrewUi.theme`,
 * documented in `crew-customer-ui/THEMING.md`): `:root` declarations of the
 * listed custom properties, and rules on the listed `data-component` values.
 * `.text-brand` is the one exception: the portal has no `data-component` for
 * brand-colored accents. Outstanding contract gaps are tracked in https://app.clickup.com/t/868m5n5q0.
 *
 * The output is wrapped in `@layer custom`, which sorts past Tailwind's utilities
 * and so wins without `!important`. The five brand/surface tokens the portal
 * re-declares unlayered are the exception, marked at their declarations.
 *
 * `--radius-*` and `--shadow-*` are never overridden — radius tokens would
 * restyle every `rounded-*` element, and Tailwind inlines `shadow-*` at build
 * time, so those tokens are inert. Both are emitted as explicit rules instead.
 */
/**
 * Surface vs. overlay shadow per level. Overlays sit one step heavier so a
 * dropdown keeps reading as floating above the content.
 */
const SURFACE_SHADOW = {
    none: 'none',
    sm: '0 1px 3px rgba(0,0,0,0.08)',
    md: '0 4px 12px rgba(0,0,0,0.10)',
    lg: '0 10px 30px rgba(0,0,0,0.12)',
    xl: '0 20px 50px rgba(0,0,0,0.16)',
};
const OVERLAY_SHADOW = {
    none: 'none',
    sm: '0 4px 12px rgba(0,0,0,0.10)',
    md: '0 10px 30px rgba(0,0,0,0.12)',
    lg: '0 18px 45px rgba(0,0,0,0.16)',
    xl: '0 24px 60px rgba(0,0,0,0.20)',
};
/** "Halo" style — hairline ring + soft near shadow + large diffuse, scaled by level. */
const HALO_SHADOW = {
    none: 'none',
    sm: '0 0 0 1px color-mix(in srgb, black 5%, transparent), 0 1px 4px rgba(0,0,0,0.04), 0 6px 16px -4px rgba(0,0,0,0.08)',
    md: '0 0 0 1px color-mix(in srgb, black 6%, transparent), 0 2px 8px rgba(0,0,0,0.06), 0 12px 28px -6px rgba(0,0,0,0.12)',
    lg: '0 0 0 1px color-mix(in srgb, black 6%, transparent), 0 2px 8px rgba(0,0,0,0.06), 0 16px 40px -8px rgba(0,0,0,0.18)',
    xl: '0 0 0 1px color-mix(in srgb, black 7%, transparent), 0 4px 14px rgba(0,0,0,0.08), 0 24px 60px -10px rgba(0,0,0,0.24)',
};
/** Glass panel shadow per level; `md` is the value tuned against a real merchant photo. */
const GLASS_SHADOW = {
    none: 'none',
    sm: '0 4px 12px rgb(0 0 0 / 0.08)',
    md: '0 8px 24px rgb(0 0 0 / 0.12)',
    lg: '0 12px 32px rgb(0 0 0 / 0.16)',
    xl: '0 20px 48px rgb(0 0 0 / 0.2)',
};
/** Ratios fanning the four control sizes out to the 12 emitted type steps. */
const scale = (theme) => {
    const { headingSize, subheadingSize, bodySize, labelSize } = theme;
    return {
        headingLg: Math.round(headingSize * 1.25),
        headingMd: headingSize,
        headingSm: Math.round(headingSize * 0.833),
        subheadingLg: Math.round(subheadingSize * 1.125),
        subheadingMd: subheadingSize,
        subheadingSm: Math.round(subheadingSize * 0.875),
        bodyLg: Math.round(bodySize * 1.143),
        bodyMd: bodySize,
        bodySm: Math.round(bodySize * 0.857),
        labelLg: labelSize + 4,
        labelMd: labelSize + 2,
        labelSm: labelSize,
    };
};
const leading = (sizePx, ratio) => `${((sizePx * ratio) / 16).toFixed(3)}rem`;
/** Appends a field's gradient stops, if any, as a 135° linear gradient. */
const resolveColor = (value, field, gradients) => {
    const stops = gradients[field];
    if (!stops?.length)
        return value;
    return `linear-gradient(135deg, ${[value, ...stops].join(', ')})`;
};
/**
 * Frosted glass derives translucency from the solid surface colors, so every color
 * in the theme stays a plain 6-digit hex like the portal's own tokens.
 */
const glassify = (hex, opacity) => {
    const [r, g, b] = channels(hex);
    return `rgb(${r} ${g} ${b} / ${opacity})`;
};
/** Google Fonts `@import` for each non-system family, deduped. */
const fontImports = (theme) => {
    const seen = new Set();
    return [theme.headingFontFamily, theme.fontFamily].flatMap((family) => {
        const name = /'([^']+)'/.exec(family)?.[1];
        if (!name || seen.has(name))
            return [];
        seen.add(name);
        // css2 rejects the whole request if any listed weight doesn't exist
        const shipped = fontWeights(family);
        const inRange = shipped.filter((weight) => weight >= 300 && weight <= 600);
        const closest = shipped.reduce((best, weight) => Math.abs(weight - 400) < Math.abs(best - 400) ? weight : best, shipped[0] ?? 400);
        const weights = inRange.length ? inRange : [closest];
        return [
            `@import url('https://fonts.googleapis.com/css2?family=${name.replaceAll(' ', '+')}:wght@${weights.join(';')}&display=swap');`,
        ];
    });
};
// tuned against a real merchant photo: outer panels at glassOpacity, the header and inner card a step more opaque, every other surface solid
export const glassOpacities = (theme) => {
    const outer = theme.backdropBlur ? theme.glassOpacity : 1;
    const header = Math.min(1, Math.round((outer + 0.1) * 100) / 100);
    return {
        surfaceContainerColor: outer,
        header,
        // what the page card's text sits on, so readability checks it as the cards
        surfaceContainerLowestColor: theme.backdropBlur
            ? (theme.glassCardOpacity ?? header)
            : 1,
    };
};
// a rolling hash, enough to notice a hand edit inside the generated block
const checksum = (text) => text
    .split('')
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 4_294_967_291, 7)
    .toString(36);
export const generateCss = (theme, { embedded = false } = {}) => {
    const { gradients } = theme;
    const opacity = glassOpacities(theme);
    const translucent = (hex, alpha) => alpha < 1 ? glassify(hex, alpha) : hex;
    // an embedded portal, and glass anywhere, leaves the shell to the merchant's page when in their iframe
    const shell = embedded || theme.backdropBlur
        ? `:root:not([data-embedded="embedded"]) [data-component="corso-app-shell"]`
        : '[data-component="corso-app-shell"]';
    const brand = resolveColor(theme.buttonPrimaryBg, GradientField.buttonPrimaryBg, gradients);
    const pageBg = resolveColor(theme.pageBackground, GradientField.pageBackground, gradients);
    const headingFont = theme.headingFontFamily;
    const bodyFont = theme.fontFamily;
    const weight = theme.fontWeight;
    // Weights below 500 would make "medium" steps lighter than their siblings.
    const mediumWeight = `${Math.max(Number.parseInt(weight, 10), 500)}`;
    const size = scale(theme);
    const containerShadow = theme.shadowStyle === ShadowStyle.halo
        ? HALO_SHADOW[theme.shadowLevel]
        : SURFACE_SHADOW[theme.shadowLevel];
    const overlayShadow = OVERLAY_SHADOW[theme.shadowLevel];
    const imports = fontImports(theme);
    const lines = [
        '/* Corso Portal – Custom CSS Variables */',
        '/* Paste into: corso-theme-styles or Custom CSS field */',
        '',
        ...(imports.length ? [...imports, ''] : []),
        '@layer custom {',
        '',
        ':root {',
        '',
        '  /* ── Color Palette ── */',
        '  /* The portal appends its own unlayered `:root` overrides for these five',
        '     after this stylesheet, and unlayered beats any @layer regardless of',
        '     order — `!important` is the only thing that outranks them. */',
        `  --color-brand: ${theme.buttonPrimaryBg} !important;`,
        `  --color-on-brand: ${theme.buttonPrimaryText} !important;`,
        `  --color-brand-container: ${theme.brandContainerColor} !important;`,
        `  --color-on-brand-container: ${theme.onBrandContainerColor} !important;`,
        '',
        `  --color-plain: ${theme.plainColor};`,
        `  --color-on-plain: ${theme.onPlainColor};`,
        `  --color-plain-container: ${theme.plainContainerColor};`,
        `  --color-on-plain-container: ${theme.onPlainContainerColor};`,
        '',
        `  --color-emphasis: ${theme.emphasisColor};`,
        `  --color-on-emphasis: ${theme.onEmphasisColor};`,
        `  --color-emphasis-container: ${theme.emphasisContainerColor};`,
        `  --color-on-emphasis-container: ${theme.onEmphasisContainerColor};`,
        '',
        `  --color-critical: ${theme.criticalColor};`,
        `  --color-on-critical: ${theme.onCriticalColor};`,
        `  --color-critical-container: ${theme.criticalContainerColor};`,
        `  --color-on-critical-container: ${theme.onCriticalContainerColor};`,
        '',
        `  --color-surface: ${theme.surfaceColor} !important;`,
        `  --color-surface-container-lowest: ${theme.surfaceContainerLowestColor};`,
        `  --color-surface-container-low: ${theme.surfaceContainerLowColor};`,
        `  --color-surface-container: ${translucent(theme.surfaceContainerColor, opacity.surfaceContainerColor)};`,
        `  --color-surface-container-high: ${theme.surfaceContainerHighColor};`,
        `  --color-surface-container-highest: ${theme.surfaceContainerHighestColor};`,
        '',
        `  --color-on-surface: ${theme.textPrimaryColor};`,
        `  --color-on-surface-variant: ${theme.textSecondaryColor};`,
        `  --color-outline: ${theme.inputBorderColor};`,
        `  --color-outline-variant: ${theme.borderColor};`,
        '',
        `  --color-inverse-surface: ${theme.inverseSurfaceColor};`,
        `  --color-inverse-on-surface: ${theme.inverseOnSurfaceColor};`,
        `  --color-scrim: ${theme.scrimColor};`,
        '',
        '  /* ── Font Families ── */',
        `  --font-heading-family: ${headingFont};`,
        `  --font-body-family: ${bodyFont};`,
        '',
        '  /* ── Heading ── */',
        `  --heading-family-lg: ${headingFont};`,
        `  --heading-size-lg: ${size.headingLg}px;`,
        `  --heading-weight-lg: ${weight};`,
        `  --heading-leading-lg: ${leading(size.headingLg, 1.2)};`,
        `  --heading-family-md: ${headingFont};`,
        `  --heading-size-md: ${size.headingMd}px;`,
        `  --heading-weight-md: ${weight};`,
        `  --heading-leading-md: ${leading(size.headingMd, 1.333)};`,
        `  --heading-family-sm: ${headingFont};`,
        `  --heading-size-sm: ${size.headingSm}px;`,
        `  --heading-weight-sm: ${weight};`,
        `  --heading-leading-sm: ${leading(size.headingSm, 1.4)};`,
        '',
        '  /* ── Subheading ── */',
        `  --subheading-family-lg: ${headingFont};`,
        `  --subheading-size-lg: ${size.subheadingLg}px;`,
        `  --subheading-weight-lg: ${weight};`,
        `  --subheading-leading-lg: ${leading(size.subheadingLg, 1.4)};`,
        `  --subheading-family-md: ${bodyFont};`,
        `  --subheading-size-md: ${size.subheadingMd}px;`,
        `  --subheading-weight-md: ${mediumWeight};`,
        `  --subheading-leading-md: ${leading(size.subheadingMd, 1.5)};`,
        `  --subheading-family-sm: ${bodyFont};`,
        `  --subheading-size-sm: ${size.subheadingSm}px;`,
        `  --subheading-weight-sm: ${mediumWeight};`,
        `  --subheading-leading-sm: ${leading(size.subheadingSm, 1.43)};`,
        '',
        '  /* ── Body ── */',
        `  --body-family-lg: ${bodyFont};`,
        `  --body-size-lg: ${size.bodyLg}px;`,
        `  --body-weight-lg: ${weight};`,
        `  --body-leading-lg: ${leading(size.bodyLg, 1.5)};`,
        `  --body-family-md: ${bodyFont};`,
        `  --body-size-md: ${size.bodyMd}px;`,
        `  --body-weight-md: ${weight};`,
        `  --body-leading-md: ${leading(size.bodyMd, 1.43)};`,
        `  --body-family-sm: ${bodyFont};`,
        `  --body-size-sm: ${size.bodySm}px;`,
        `  --body-weight-sm: ${weight};`,
        `  --body-leading-sm: ${leading(size.bodySm, 1.33)};`,
        '',
        '  /* ── Label ── */',
        `  --label-family-lg: ${bodyFont};`,
        `  --label-size-lg: ${size.labelLg}px;`,
        `  --label-weight-lg: ${mediumWeight};`,
        `  --label-leading-lg: ${leading(size.labelLg, 1.25)};`,
        `  --label-family-md: ${bodyFont};`,
        `  --label-size-md: ${size.labelMd}px;`,
        `  --label-weight-md: ${mediumWeight};`,
        `  --label-leading-md: ${leading(size.labelMd, 1.14)};`,
        `  --label-family-sm: ${bodyFont};`,
        `  --label-size-sm: ${size.labelSm}px;`,
        `  --label-weight-sm: ${mediumWeight};`,
        `  --label-leading-sm: ${leading(size.labelSm, 1.33)};`,
        '',
        '}',
        '',
    ];
    // Page background — `corso-app-shell` is the root wrapper of the app. The
    // longhands are deliberate: the `background` shorthand would reset the
    // merchant's banner image the shell carries.
    if (theme.pageBackgroundImage) {
        lines.push('/* ===== Page Background Image ===== */', `${shell} {`, `  background-image: url("${theme.pageBackgroundImage.replaceAll(/["\\\n\r]/g, encodeURIComponent)}");`, '  background-size: cover;', `  background-position: ${theme.pageBackgroundFocalX}% ${theme.pageBackgroundFocalY}%;`, '}', '');
    }
    else if (pageBg.includes('gradient')) {
        lines.push('/* ===== Page Background Gradient ===== */', `${shell} {`, 
        // the shorthand would reset the merchant's banner image
        `  background-image: ${pageBg};`, '}', '');
    }
    else {
        lines.push('/* ===== Page Background Color ===== */', `${shell} {`, `  background-color: ${theme.pageBackground};`, '}', '');
    }
    // App body padding — `corso-app-body` centers header + content + footer
    // inside `corso-app-shell`.
    if (theme.appBodyPaddingY > 0) {
        lines.push('/* ===== App Body Padding ===== */', '[data-component="corso-app-body"] {', `  padding-top: ${theme.appBodyPaddingY}px;`, `  padding-bottom: ${theme.appBodyPaddingY}px;`, '}', '');
    }
    // Card padding — a free-form CSS padding shorthand on the inner page card, so
    // one/two/four-value forms (`24px`, `10px 5px 15px 20px`) all work. Anything
    // outside a length literal is dropped: a value that could close this rule or
    // open a comment would swallow every rule after it.
    const cardPadding = theme.cardPadding
        .replaceAll(/[^\d\s.a-z%-]/gi, '')
        .trim();
    if (cardPadding) {
        lines.push('/* ===== Card Padding ===== */', '[data-component="corso-page"] {', `  padding: ${cardPadding};`, '}', '');
    }
    // Section spacing — `corso-page-section` is a `flex flex-col gap-4` column, so
    // the gap between its children is what this control means.
    lines.push('/* ===== Section Spacing ===== */', '[data-component="corso-page-section"] {', `  gap: ${theme.sectionSpacing}px;`, '}', '', '/* ===== Border Radius ===== */', '[data-component="corso-app-header"],', '[data-component="corso-app-content"],', '[data-component="corso-page"],', '[data-component="corso-radio-card-option"],', '[data-component="corso-dropzone"],', '[data-component="corso-combobox-listbox"],', '[data-component="corso-product-filter-popover"],', '[data-component="corso-product-search-popover"] {', `  border-radius: ${theme.borderRadius}px;`, '}', '', '/* ===== Button Border Radius ===== */', '[data-component="corso-button"] {', `  border-radius: ${theme.buttonBorderRadius}px;`, '}', '', '/* ===== Field Border Radius ===== */', '[data-component="corso-combobox"],', 
    // The combobox's backing <input> sits `absolute inset-0` behind the button
    // and carries no `type` attribute (real text inputs are `type="text"`).
    // Round it too, or its square corners show through the rounded button.
    '[data-component="corso-field"] input:where(:not([type])),', '[data-component="corso-textarea"],', 
    // Exclude checkboxes — they keep their own small radius; the theme radius
    // would round them into a circle at larger values.
    '[data-component="corso-input"]:not([type="checkbox"]) {', `  border-radius: ${theme.fieldBorderRadius}px;`, '}', '', 
    // Re-pointing the two tokens the outlined variant reads, rather than setting
    // `border-color` / `color`, keeps this rule out of the color cascade — so the
    // variant's own disabled utilities still win on specificity.
    '/* ===== Outlined Button ===== */', '[data-component="corso-button"][data-variant="outlined"] {', `  --color-outline-variant: ${theme.buttonOutlineBorder};`, `  --color-on-surface-variant: ${theme.buttonOutlineText};`, '}', '');
    // Form controls (input / textarea / combobox) are intentionally NOT bordered:
    // crew-customer-ui renders them `border-none` with a `ring-1 ring-outline`, so
    // adding a border draws a second outline on top of the ring. Their outline
    // color is themed via `--color-outline` (Inner Input Outline), which the ring
    // already reads.
    // Brand gradient — `:root --color-brand` covers the solid case; gradients need
    // an explicit `background` declaration on filled buttons.
    if (brand.includes('gradient')) {
        lines.push('/* ===== Brand Gradient (Filled Buttons) ===== */', 
        // :enabled keeps the variant's disabled fill; the unpressed toggle has its own
        '[data-component="corso-button"][data-variant="filled"]:enabled:not([aria-pressed="false"]),', '[data-component="corso-icon-button"][data-variant="filled"]:enabled:not([aria-pressed="false"]) {', `  background: ${brand};`, `  color: ${theme.buttonPrimaryText};`, '}', '');
    }
    // Input field background — shared across text inputs, textareas, comboboxes
    // (trigger + search), the file-upload dropzone, and radio cards so every field
    // surface matches. A checkbox is also a `corso-input`; it keeps its own
    // checked/unchecked fills.
    lines.push('/* ===== Input Background ===== */', '[data-component="corso-input"]:not([type="checkbox"]),', '[data-component="corso-textarea"],', '[data-component="corso-combobox-search"],', '[data-component="corso-combobox"],', '[data-component="corso-dropzone"] {', `  background-color: ${theme.inputBackgroundColor};`, '}', '', 
    // Re-pointing the token its hover/focus `bg-mix` reads keeps those states
    '[data-component="corso-radio-card-option"] {', `  --color-surface-container-lowest: ${theme.inputBackgroundColor};`, '}', '', 
    // The combobox's backing <input> (typeless, absolute inset-0 behind the
    // button) inherits the @tailwindcss/forms reset's opaque `#fff`, which shows
    // straight through the translucent combobox button on glass. The visible
    // background comes from the combobox button, so keep this transparent.
    '/* ===== Combobox Backing Input ===== */', '[data-component="corso-field"] input:where(:not([type])) {', '  background-color: transparent;', '}', '', 
    // Overlays keep the theme's solid surface colors even when glass makes the
    // `:root` tokens translucent — page content must never read through an open
    // dropdown or popover. Each takes the token its portal component paints with.
    '/* ===== Overlays ===== */', '[data-component="corso-combobox-listbox"] {', `  background-color: ${theme.surfaceContainerLowColor};`, `  box-shadow: ${overlayShadow};`, '}', '', '[data-component="corso-product-filter-popover"],', '[data-component="corso-product-search-popover"] {', `  background-color: ${theme.surfaceContainerLowestColor};`, `  box-shadow: ${overlayShadow};`, '}', '');
    // Container + card shadow, on the two outer wrappers plus the inner page
    // card so the card visibly floats (it has no native shadow). Radio cards are
    // left alone — they toggle `shadow-none` on check, and a `@layer custom` rule
    // would defeat that. When backdrop blur is on, the glass block below sets the
    // panel shadow instead.
    if (!theme.backdropBlur && theme.cardSurfaces) {
        lines.push('/* ===== Shadows ===== */', '[data-component="corso-app-header"],', '[data-component="corso-app-content"],', '[data-component="corso-page"] {', `  box-shadow: ${containerShadow};`, '}', '');
    }
    if (embedded && !theme.backdropBlur) {
        lines.push('/* ===== Embedded ===== */', ':root[data-embedded="embedded"] {', '  --color-surface: transparent !important;', '}', '');
    }
    // Glass, tuned against a real merchant photo.
    // The content panel paints from its translucent token; the header and card are painted here.
    if (theme.backdropBlur) {
        const edge = theme.cardBorders ? 'rgb(255 255 255 / 0.5)' : 'transparent';
        const glassShadow = [
            theme.shadowStyle === ShadowStyle.halo
                ? HALO_SHADOW[theme.shadowLevel]
                : GLASS_SHADOW[theme.shadowLevel],
            theme.glassHighlight && 'inset 0 1px 0 rgb(255 255 255 / 0.4)',
        ]
            .filter((shadow) => shadow && shadow !== 'none')
            .join(', ') || 'none';
        lines.push(
        // the merchant's page is the background; the blur can't reach through the
        // iframe, so embedded glass is see-through rather than frosted
        '/* ===== Glass (Embedded) ===== */', ':root[data-embedded="embedded"] {', '  --color-surface: transparent !important;', '}', '', 
        // room for the panel shadows, unless the theme sets its own padding
        ...(theme.appBodyPaddingY
            ? []
            : [
                ':root[data-embedded="embedded"] [data-component="corso-app-body"] {',
                '  padding-top: 16px;',
                '  padding-bottom: 16px;',
                '}',
                '',
            ]), '/* ===== Glass (Panels) ===== */', '[data-component="corso-app-header"],', '[data-component="corso-app-content"] {', '  backdrop-filter: blur(20px) saturate(1.4);', '  -webkit-backdrop-filter: blur(20px) saturate(1.4);', `  border: 1px solid ${edge};`, 
        // the next three stop a light strip along the bottom edge in some browsers
        '  border-bottom-color: transparent;', '  background-clip: padding-box;', '  isolation: isolate;', `  box-shadow: ${glassShadow};`, '}', '', 
        // the header and card read the same token as popovers, which stay solid, so they're painted here
        '[data-component="corso-app-header"] {', `  background-color: ${translucent(theme.surfaceContainerLowestColor, opacity.header)};`, '}', '', 
        // the inner card is its own stacked pane: its own fill, edge and shadow
        '[data-component="corso-page"] {', `  background-color: ${translucent(theme.surfaceContainerLowestColor, opacity.surfaceContainerLowestColor)};`, '  backdrop-filter: blur(12px);', '  -webkit-backdrop-filter: blur(12px);', `  border: 1px solid ${edge};`, `  box-shadow: ${glassShadow};`, '}', '', 
        // Nested surfaces inside the frosted page card — disclosures and the
        // description list's row fills — would stack their own alpha on top of
        // the card's and read as denser bands. Let the card show through them.
        '/* ===== Glass (Nested Surfaces) ===== */', '[data-component="corso-disclosure"],', '[data-component="corso-description-list"],', '[data-component="corso-description-list"] > dt,', '[data-component="corso-description-list"] > dd {', '  background-color: transparent;', '}', '', 
        // the list draws its row dividers by showing its own fill through a 1px
        // gap, which the transparent fill above erases; draw them as borders
        '[data-component="corso-description-list"] > dt:not(:first-of-type),', '[data-component="corso-description-list"] > dd:not(:first-of-type) {', '  border-top: 1px solid color-mix(in srgb, var(--color-outline-variant) 55%, transparent);', '}', '');
    }
    if (!theme.cardBorders) {
        lines.push('/* ===== No Card Borders ===== */', '[data-component="corso-app-header"],', '[data-component="corso-app-content"],', '[data-component="corso-page"] {', '  border-color: transparent;', '}', '');
    }
    // the page shows through where the panels and cards would be
    if (!theme.cardSurfaces) {
        lines.push('/* ===== Card-free Layout ===== */', '[data-component="corso-app-header"],', '[data-component="corso-app-content"],', '[data-component="corso-page"],', '[data-component="corso-page-section"] {', '  background-color: transparent;', '  box-shadow: none;', '}', '');
    }
    if (theme.flatDisclosures) {
        lines.push('/* ===== Flat Collapsible Rows ===== */', '[data-component="corso-disclosure"] {', '  background-color: transparent;', '  border-width: 1px 0 0;', '  border-radius: 0;', '  box-shadow: none;', '  padding-inline: 0;', '}', '');
    }
    lines.push(
    // Brand text — overrides the `.text-brand` utility used by links and other
    // brand-colored accents, which compiles to `color: var(--color-brand)`. This
    // also recolors ghost buttons, whose variant is `bg-transparent text-brand`.
    '/* ===== Brand Text ===== */', '.text-brand {', `  color: ${theme.brandTextColor};`, '}', '', '}');
    const body = lines.join('\n');
    // lets the next run start from this theme and keep what was added around it; URI encoding leaves no `{`, `}` or `/` to open or close anything
    return [
        `/* corso-portal-theme ${encodeURIComponent(JSON.stringify(embedded ? { ...theme, embedded } : theme))} */`,
        body,
        `/* corso-portal-theme end ${checksum(body)} */`,
    ].join('\n');
};
const markerSchema = portalTheme.extend({ embedded: z.boolean().optional() });
const decodeMarker = (encoded) => {
    try {
        const marker = markerSchema.safeParse(JSON.parse(decodeURIComponent(encoded))).data;
        if (!marker)
            return undefined;
        const { embedded, ...theme } = marker;
        // the marker is editable and its fonts are written raw into the CSS, but the schema only checks their shape
        return isFontStack(theme.fontFamily) && isFontStack(theme.headingFontFamily)
            ? { theme, embedded: embedded ?? false }
            : undefined;
    }
    catch {
        return undefined;
    }
};
/**
 * Splits saved Custom CSS into the theme `generateCss` wrote, the CSS around
 * that block (Settings → Theme prepends fonts and appends classes), and
 * whatever can't be carried over: the whole CSS when it has no generated block,
 * or the block when someone edited inside it.
 */
export const readPortalCss = (saved) => {
    const css = saved.replaceAll('\r\n', '\n');
    const block = /\/\* corso-portal-theme (\S+) \*\/\n([\s\S]*)\n\/\* corso-portal-theme end (\w+) \*\//.exec(css);
    const marker = block && decodeMarker(block[1] ?? '');
    if (!block || !marker)
        return {
            theme: undefined,
            embedded: false,
            kept: '',
            replaced: css.trim(),
        };
    const kept = [
        css.slice(0, block.index),
        css.slice(block.index + block[0].length),
    ]
        .map((part) => part.trim())
        .filter(Boolean)
        .join('\n\n');
    const edited = checksum(block[2] ?? '') !== block[3];
    return { ...marker, kept, replaced: edited ? block[0] : '' };
};
// `@import` must precede every rule, so the kept CSS's imports move above the block
// a whole statement: Google Fonts URLs carry `;` between weights
const IMPORT = /@import\s+(?:url\(\s*(?:"[^"]*"|'[^']*'|[^)]*)\s*\)|"[^"]*"|'[^']*')[^;]*;/g;
export const withKeptCss = (css, kept) => {
    const imports = kept.match(IMPORT) ?? [];
    const rest = kept.replaceAll(IMPORT, '').trim();
    return [...imports, css, rest].filter(Boolean).join('\n\n');
};
//# sourceMappingURL=generateCss.js.map